<template>
	<div class="d-flex flex-column align-items-center my-4">
		<apple-sign-in-button
			v-if="showAppleSignIn"
			class="mb-3"
			@success="onAppleSuccess"
			@error="onAppleError" />

		<google-sign-in-button
			clientId="907876553651-qujta0capc617s9p8cqgv3u24uhe45it.apps.googleusercontent.com"
			@success="onGoogleSuccess"
			@error="onGoogleError" />

		<!-- <facebook-login-button
			appId="1143129803648680"
			@success="onFacebookSuccess"
			@error="onFacebookError" /> -->

		<small class="mt-2 text-center fst-italic">
			By using these buttons, you confirm you are 13 or older and agree to our
			<router-link to="/terms">Terms of Use</router-link>
			and
			<router-link to="/privacy-policy">Privacy Policy</router-link>.
		</small>
	</div>
</template>

<script setup>
	import { RouterLink, useRouter } from "vue-router"
	import { computed } from "vue"
	import { Capacitor } from "@capacitor/core"
	import AppleSignInButton from "@/components/AppleSignInButton.vue"
	import GoogleSignInButton from "@/components/GoogleSignInButton.vue"
	// import FacebookLoginButton from "@/components/FacebookLoginButton.vue"
	import Swal from "sweetalert2"
	import { startLoading, stopLoading } from "@/utils/loaderBus"
	import { useTokenStore } from "@/stores/token"

	const tokenStore = useTokenStore()
	const router = useRouter()
	const showAppleSignIn = computed(() => Capacitor.getPlatform() === "ios")

	async function onAppleSuccess(payload) {
		if (!payload.identityToken || !payload.user) {
			Swal.fire({
				icon: "error",
				title: "Could not complete Apple sign in",
			})
			return
		}

		startLoading()
		try {
			const resp = await fetch("/api/oauth", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					type: "apple",
					identity_token: payload.identityToken,
					apple_user: payload.user,
					email: payload.email,
					name: [payload.givenName, payload.familyName]
						.filter(Boolean)
						.join(" ")
						.trim(),
				}),
			})
			const data = await resp.json()
			if (data.message || data.error) {
				Swal.fire({ icon: "error", title: data.message ?? data.error })
				return
			}
			await tokenStore.setTokens(data.access_token, data.refresh_token)
			router.push({ name: "dashboard" })
		} catch (e) {
			Swal.fire({ icon: "error", title: e.message })
		} finally {
			stopLoading()
		}
	}

	// Called when the component emits 'success'
	async function onGoogleSuccess({ token }) {
		console.log("Google success:", token)
		if (!token) {
			Swal.fire({
				icon: "error",
				title: "Could not get token",
			})
			return
		}

		startLoading()
		try {
			const resp = await fetch("/api/oauth", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					google_token: token,
					type: "google",
				}),
			})
			const data = await resp.json()
			if (data.message || data.error) {
				Swal.fire({ icon: "error", title: data.message ?? data.error })
				return
			}
			await tokenStore.setTokens(data.access_token, data.refresh_token)
			router.push({ name: "dashboard" })
		} catch (e) {
			Swal.fire({ icon: "error", title: e.message })
		} finally {
			stopLoading()
		}
	}

	// Called if the Google SDK fails to load
	function onGoogleError({ message }) {
		// Swal.fire({ icon: "error", title: message })
		console.log(message)
	}

	function onAppleError({ message }) {
		if (message === "Sign in cancelled") {
			return
		}
		Swal.fire({ icon: "error", title: message })
	}

	async function onFacebookSuccess(payload) {
		if (!payload.access_token) {
			Swal.fire({ icon: "error", title: "Could not get Facebook access token" })
			return
		}
		startLoading()
		try {
			const resp = await fetch("/api/oauth", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					facebook_data: payload,
					type: "facebook",
				}),
			})
			const data = await resp.json()
			if (data.message || data.error) {
				Swal.fire({ icon: "error", title: data.message ?? data.error })
				return
			}
			await tokenStore.setTokens(data.access_token, data.refresh_token)
			router.push({ name: "dashboard" })
		} catch (e) {
			Swal.fire({ icon: "error", title: e.message })
		} finally {
			stopLoading()
		}
	}

	function onFacebookError({ message }) {
		Swal.fire({ icon: "error", title: message })
	}
</script>
